import FormField from 'components/FormField';
import queryKeys from 'config/queryKeys';
import KoalaButton from 'koala/components/Button';
import React, { useState } from 'react';
import { useMutation, useQuery, useQueryCache } from 'react-query';
import { Outcome } from 'types';
import * as remoteApi from 'api/remote';
import KoalaLoader from 'koala/components/Loader';
import theme from 'theme';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import ChartSourceType from './ChartSourceType';

const PreviewContainer = styled.div`
  margin-top: ${theme.spacing.x2};
`;

interface CurrentValueProps {
  outcome: Outcome;
}

// Component that loads the current value of from the data source
function CurrentValue(props: CurrentValueProps) {
  const { outcome } = props;

  // Query keys and query params
  const queryKey = [
    queryKeys.currentOutcome,
    outcome.id,
    `data_source:amplitude:${outcome.data_source_type}`,
    'current',
  ];
  const staleTime = 300;

  const { data, isLoading, isFetching } = useQuery(queryKey, remoteApi.fetchOutcomeDataSourceCurrentValue, {
    staleTime,
  });

  if (isLoading || isFetching) {
    return (
      <FormField>
        <label>Preview value</label>
        <KoalaLoader />
      </FormField>
    );
  }

  const currentValue = data?.data.result;

  if (currentValue === null || currentValue === undefined) {
    return <FormField>Sorry, an error occurred while fetching the current value.</FormField>;
  }

  const dataTypeToLabel: any = {
    avg_session_length: '<number> seconds',
    avg_session_per_user: '<number> sessions per user',
    active_users: '<number> active users',
    new_users: '<number> new users',
    chart: '<number>',
  };

  const label = dataTypeToLabel[outcome.data_source_type || ''];
  const labelWithNumber = label.replace('<number>', currentValue.toLocaleString());
  return (
    <FormField>
      <label>Preview value</label>
      <p>{labelWithNumber}</p>
    </FormField>
  );
}

interface Props {
  outcome: Outcome;
  workspaceSlug: string;
}

// Component that loads the current value of from the data source
function AmplitudeSource(props: Props) {
  const { outcome, workspaceSlug } = props;
  const queryCache = useQueryCache();
  const [data_source_type, setDataSourceType] = useState(outcome.data_source_type || '');
  const { t } = useTranslation();
  const translationKey = 'modals.dataConnectors.amplitudeData';

  // Functions to update the outcome
  const [updateOutcomeDataSourceMutation, { isLoading: isUpdating }] = useMutation(remoteApi.updateOutcomeDataSource, {
    onSuccess: () => {
      queryCache.invalidateQueries(queryKeys.currentOutcome);
    },
  });

  const handleUpdate = (e: any) => {
    e.preventDefault();

    let body = {
      data_source_origin: 'amplitude',
      data_source_type: data_source_type,
      data_source_meta: {},
    };

    const mutationParams = {
      outcomeId: outcome.nano_slug,
      body,
    };
    updateOutcomeDataSourceMutation(mutationParams);
  };

  const availableDataSourceTypes = ['chart', 'active_users', 'new_users', 'avg_session_length', 'avg_session_per_user'];

  const hasChanged = data_source_type !== outcome.data_source_type;
  return (
    <>
      <FormField>
        <label>{t(`modals.dataConnectors.changeMetric`)}</label>
        <select value={data_source_type} onChange={(e) => setDataSourceType(e.currentTarget.value)}>
          <option value="">{t(`modals.dataConnectors.selectMetric`)}</option>
          <option value="chart">{t(`${translationKey}.chart`)}</option>
          <option value="active_users">{t(`${translationKey}.activeUsers`)}</option>
          <option value="new_users">{t(`${translationKey}.newUsers`)}</option>
          <option value="avg_session_length">{t(`${translationKey}.avgSessionLength`)}</option>
          <option value="avg_session_per_user">{t(`${translationKey}.avgSessionUser`)}</option>
        </select>
      </FormField>

      {data_source_type === 'chart' && <ChartSourceType workspaceSlug={workspaceSlug} outcome={outcome} />}

      <PreviewContainer>
        {hasChanged && data_source_type !== 'chart' && (
          <KoalaButton
            onClick={handleUpdate}
            loading={isUpdating}
            disabled={!data_source_type || isUpdating}
            appearance="subtle"
          >
            {t(`panels.editOutcome.savePreview`)}
          </KoalaButton>
        )}
        {outcome.data_source_origin === 'amplitude' &&
          outcome.data_source_type &&
          outcome.data_source_type === data_source_type && (
            <>{availableDataSourceTypes.includes(outcome.data_source_type) && <CurrentValue outcome={outcome} />}</>
          )}
      </PreviewContainer>
    </>
  );
}

export default React.memo(AmplitudeSource);
